import { useState, useMemo } from 'react';
import { Plus, Search, Package, CheckCircle, AlertCircle, X, Upload } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useEmployees } from '@/api/queries/employee.queries';
import { useMaterials, useIssueMaterial, useUpdateMaterial } from '@/api/queries/document.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { format } from 'date-fns';
import { toast } from 'sonner';

const CATEGORIES = ['Laptop', 'Phone', 'SIM Card', 'Furniture', 'Access Card', 'Vehicle', 'Other'];
const CONDITIONS  = ['Good', 'Fair', 'Damaged'];

function StatusBadge({ status }) {
  const styles = {
    ACTIVE:   'bg-green-100 text-green-700',
    RETURNED: 'bg-gray-100 text-gray-600',
    LOST:     'bg-red-100 text-red-700',
    DAMAGED:  'bg-amber-100 text-amber-700',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold', styles[status] || styles.ACTIVE)}>
      {status}
    </span>
  );
}

function IssueMaterialModal({ onClose, onSuccess }) {
  const { data } = useEmployees({ limit: 200 });
  const employees  = data?.data || [];
  const issueMaterial = useIssueMaterial();

  const [form, setForm] = useState({
    employee_id: '', material_name: '', category: 'Laptop',
    serial_number: '', asset_code: '', issue_date: new Date().toISOString().slice(0, 10),
    expected_return: '', cost_value: '', condition: 'Good', notes: '', photo_url: '',
  });
  const [empSearch, setEmpSearch] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);

  const filteredEmps = useMemo(() => {
    const q = empSearch.toLowerCase();
    return employees.filter(e => {
      const n = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
      return !q || n.includes(q) || (e.employee_code || '').toLowerCase().includes(q);
    });
  }, [employees, empSearch]);

  const selEmp = employees.find(e => e.id === form.employee_id);

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPhotoPreview(url);
    setForm(f => ({ ...f, photo_url: url }));
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.employee_id) { toast.error('Select an employee'); return; }
    if (!form.material_name.trim()) { toast.error('Enter material name'); return; }
    if (!form.serial_number.trim()) { toast.error('Enter serial number'); return; }

    const emp = employees.find(e => e.id === form.employee_id);
    const empName = `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim() || emp?.name || '';
    const deptStr = (d) => typeof d === 'string' ? d : (d?.name || '');

    issueMaterial.mutate({
      ...form,
      employee_name: empName,
      employee_code: emp?.employee_code || '',
      department:    deptStr(emp?.department),
      designation:   deptStr(emp?.designation),
      status:        'ACTIVE',
    }, {
      onSuccess: () => { toast.success('Material issued successfully'); onSuccess(); onClose(); },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)] shrink-0">
          <div>
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Issue Material to Employee</h3>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">Track company assets issued to employees</p>
          </div>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Employee selector */}
          <div>
            <label className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-1.5 block">Employee *</label>
            {selEmp ? (
              <div className="flex items-center gap-2.5 p-2.5 rounded-[10px] bg-[var(--accent-muted)] border border-[var(--accent)]/30">
                <Avatar name={`${selEmp.first_name || ''} ${selEmp.last_name || ''}`.trim()} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-medium text-[var(--text-primary)]">{`${selEmp.first_name || ''} ${selEmp.last_name || ''}`.trim()}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)]">{selEmp.employee_code}</p>
                </div>
                <button onClick={() => set('employee_id', '')} className="text-[var(--text-tertiary)] hover:text-[var(--danger)]">
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div className="border border-[var(--border)] rounded-[10px] overflow-hidden">
                <div className="relative border-b border-[var(--border)]">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
                  <input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employees…"
                    className="w-full h-9 pl-9 pr-3 text-[12px] bg-[var(--bg-app)] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none" />
                </div>
                <div className="max-h-40 overflow-y-auto">
                  {filteredEmps.slice(0, 20).map(emp => {
                    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                    return (
                      <button key={emp.id} onClick={() => { set('employee_id', emp.id); setEmpSearch(''); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--bg-hover)] transition-colors text-left">
                        <Avatar name={name} size="xs" />
                        <div>
                          <p className="text-[12px] font-medium text-[var(--text-primary)]">{name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{emp.employee_code}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 2-col grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="form-label">Material Name *</label>
              <input value={form.material_name} onChange={e => set('material_name', e.target.value)}
                placeholder="e.g. MacBook Pro 14-inch"
                className="input-field" />
            </div>
            <div>
              <label className="form-label">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className="input-field">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Serial Number *</label>
              <input value={form.serial_number} onChange={e => set('serial_number', e.target.value)}
                placeholder="e.g. MBP-001-2024"
                className="input-field" />
            </div>
            <div>
              <label className="form-label">Company Asset Code</label>
              <input value={form.asset_code} onChange={e => set('asset_code', e.target.value)}
                placeholder="e.g. ASSET-001"
                className="input-field" />
            </div>
            <div>
              <label className="form-label">Cost Value (₹)</label>
              <input type="number" value={form.cost_value} onChange={e => set('cost_value', e.target.value)}
                placeholder="e.g. 150000"
                className="input-field" />
            </div>
            <div>
              <label className="form-label">Issue Date</label>
              <input type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)}
                className="input-field" />
            </div>
            <div>
              <label className="form-label">Expected Return Date</label>
              <input type="date" value={form.expected_return} onChange={e => set('expected_return', e.target.value)}
                className="input-field" />
            </div>
            <div>
              <label className="form-label">Condition at Issue</label>
              <select value={form.condition} onChange={e => set('condition', e.target.value)} className="input-field">
                {CONDITIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="form-label">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                placeholder="Any additional details…"
                className="input-field resize-none" />
            </div>

            {/* Photo upload */}
            <div className="col-span-2">
              <label className="form-label">Photo Proof</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[8px] cursor-pointer hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
                  <Upload size={13} /> Upload Photo
                  <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
                </label>
                {photoPreview && (
                  <img src={photoPreview} alt="proof"
                    className="h-16 w-16 object-cover rounded-[8px] border border-[var(--border)]" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)] shrink-0">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={issueMaterial.isPending}
            className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
            {issueMaterial.isPending ? 'Issuing…' : 'Issue Material'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReturnModal({ material, onClose }) {
  const updateMaterial = useUpdateMaterial();
  const [form, setForm] = useState({
    return_date: new Date().toISOString().slice(0, 10),
    return_condition: material.condition || 'Good',
    return_notes: '',
    new_status: 'RETURNED',
  });

  const handleSubmit = () => {
    updateMaterial.mutate({ id: material.id, status: form.new_status, ...form }, {
      onSuccess: () => { toast.success('Material status updated'); onClose(); },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Update Material Status</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-[var(--text-secondary)]">
            <strong>{material.material_name}</strong> · {material.serial_number} · issued to <strong>{material.employee_name}</strong>
          </p>
          <div>
            <label className="form-label">Status</label>
            <select value={form.new_status} onChange={e => setForm(f => ({ ...f, new_status: e.target.value }))} className="input-field">
              <option value="RETURNED">Returned</option>
              <option value="LOST">Lost</option>
              <option value="DAMAGED">Damaged</option>
            </select>
          </div>
          <div>
            <label className="form-label">Return / Update Date</label>
            <input type="date" value={form.return_date} onChange={e => setForm(f => ({ ...f, return_date: e.target.value }))} className="input-field" />
          </div>
          <div>
            <label className="form-label">Condition</label>
            <select value={form.return_condition} onChange={e => setForm(f => ({ ...f, return_condition: e.target.value }))} className="input-field">
              {CONDITIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Notes</label>
            <textarea value={form.return_notes} onChange={e => setForm(f => ({ ...f, return_notes: e.target.value }))} rows={2} className="input-field resize-none" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-[var(--border)]">
          <button onClick={onClose} className="h-9 px-4 text-[13px] font-medium border border-[var(--border)] rounded-[10px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={updateMaterial.isPending}
            className="h-9 px-5 text-[13px] font-medium bg-[var(--accent)] text-white rounded-[10px] hover:bg-[var(--accent-hover)] disabled:opacity-60 transition-colors">
            {updateMaterial.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IssueMaterialPage() {
  const [showModal, setModal]       = useState(false);
  const [returnModal, setReturnModal] = useState(null);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useState('ALL');
  const { data, refetch }           = useMaterials();
  const rows                        = data?.data || [];

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const matchStatus = statusFilter === 'ALL' || r.status === statusFilter;
      const matchSearch = !search ||
        r.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.material_name?.toLowerCase().includes(search.toLowerCase()) ||
        r.serial_number?.toLowerCase().includes(search.toLowerCase());
      return matchStatus && matchSearch;
    });
  }, [rows, statusFilter, search]);

  const stats = useMemo(() => ({
    total:    rows.length,
    active:   rows.filter(r => r.status === 'ACTIVE').length,
    returned: rows.filter(r => r.status === 'RETURNED').length,
    lost:     rows.filter(r => r.status === 'LOST' || r.status === 'DAMAGED').length,
  }), [rows]);

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-bold text-[var(--text-primary)]">Issue Material</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">Track company assets and equipment issued to employees</p>
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-2 h-9 px-4 bg-[var(--accent)] text-white text-[13px] font-medium rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors shrink-0">
          <Plus size={15} /> Issue Material
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Issued', value: stats.total, color: 'text-[var(--text-primary)]' },
          { label: 'Active (with employees)', value: stats.active, color: 'text-green-600' },
          { label: 'Returned', value: stats.returned, color: 'text-[var(--text-secondary)]' },
          { label: 'Lost / Damaged', value: stats.lost, color: 'text-red-500' },
        ].map(s => (
          <div key={s.label} className="card p-4">
            <p className={cn('text-[22px] font-bold', s.color)}>{s.value}</p>
            <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee, material, S/N…"
            className="h-9 pl-9 pr-3 text-[12px] min-w-[220px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)]" />
        </div>
        {['ALL', 'ACTIVE', 'RETURNED', 'LOST', 'DAMAGED'].map(s => (
          <button key={s} onClick={() => setStatus(s)}
            className={cn('h-9 px-3 text-[12px] font-medium rounded-[10px] transition-all',
              statusFilter === s ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Package size={32} className="text-[var(--text-tertiary)] opacity-40" />
            <p className="text-[14px] font-medium text-[var(--text-secondary)]">No materials found</p>
            <button onClick={() => setModal(true)}
              className="h-8 px-4 text-[12px] font-medium bg-[var(--accent)] text-white rounded-[8px] hover:bg-[var(--accent-hover)] transition-colors">
              Issue First Material
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  {['Employee', 'Material', 'Category', 'Serial No.', 'Issue Date', 'Return Due', 'Condition', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={r.employee_name} size="xs" />
                        <div>
                          <p className="font-medium text-[var(--text-primary)]">{r.employee_name}</p>
                          <p className="text-[10px] text-[var(--text-tertiary)]">{r.employee_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[var(--text-primary)]">{r.material_name}</p>
                      {r.asset_code && <p className="text-[10px] text-[var(--text-tertiary)]">{r.asset_code}</p>}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{r.category}</td>
                    <td className="px-4 py-3 font-mono text-[var(--text-secondary)]">{r.serial_number}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">
                      {r.issue_date ? format(new Date(r.issue_date), 'dd MMM yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.expected_return
                        ? <span className={cn('text-[11px]', new Date(r.expected_return) < new Date() && r.status === 'ACTIVE' ? 'text-red-500 font-medium' : 'text-[var(--text-secondary)]')}>
                            {format(new Date(r.expected_return), 'dd MMM yyyy')}
                          </span>
                        : <span className="text-[var(--text-tertiary)]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{r.condition}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3">
                      {r.status === 'ACTIVE' && (
                        <button onClick={() => setReturnModal(r)}
                          className="h-7 px-2.5 text-[11px] font-medium border border-[var(--border)] rounded-[6px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
                          Update Status
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && <IssueMaterialModal onClose={() => setModal(false)} onSuccess={refetch} />}
      {returnModal && <ReturnModal material={returnModal} onClose={() => setReturnModal(null)} />}
    </div>
  );
}
